import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RotateCcw, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { errMsg, formatDate, useApi } from "@/lib/api";

const kb = (n) => `${Math.max(1, Math.round((n || 0) / 1024))} KB`;
const AREA_KEYS = ["crm", "bookings", "shop", "forms", "team", "activity", "sites", "settings", "files"];

export function NightlyAlert({ last }) {
  const stale = !last || Date.now() - new Date(last).getTime() > 26 * 3600 * 1000;
  if (!stale) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="nightly-backup-alert">
      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
      <span>Il backup automatico notturno non risulta eseguito da più di un giorno (ultimo: {last ? formatDate(last) : "mai"}). In anteprima la pianificazione può non girare: verifica sul sito pubblicato o esegui un backup manuale.</span>
    </div>);
}

function DiffTable({ diff }) {
  return (
    <table className="w-full text-[11px]" data-testid="tb-diff">
      <thead><tr className="text-left text-slate-500"><th>Dati</th><th>Nel backup</th><th>Attuali</th><th>Tornano</th><th>Spariscono</th><th>Cambiano</th></tr></thead>
      <tbody>{Object.entries(diff).map(([c, d]) => (
        <tr key={c} className={d.added_back || d.removed || d.changed ? "font-medium text-slate-900" : "text-slate-500"} data-testid={`tb-diff-${c}`}>
          <td>{c}</td><td>{d.restored}</td><td>{d.current}</td><td>{d.added_back}</td><td>{d.removed}</td><td>{d.changed}</td></tr>))}</tbody>
    </table>);
}

function RestoreDialog({ b, labels, onClose, onDone }) {
  const api = useApi();
  const [areas, setAreas] = useState(["crm", "bookings", "shop", "forms"]);
  const [diff, setDiff] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (a) => { setDiff(null); setAreas((x) => (x.includes(a) ? x.filter((y) => y !== a) : [...x, a])); };
  const preview = async () => {
    setBusy(true);
    try { setDiff((await api.post(`/tenant-backups/${b.id}/restore`, { areas, dry_run: true })).data.diff); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenant-backups/${b.id}/restore`, { areas, dry_run: false, confirm_name: name });
      if (data.status === "restored") toast.success("Ripristino completato e verificato");
      else toast.error(data.status === "rolled_back" ? `Ripristino non riuscito: stato precedente ripristinato (${data.error})` : "Ripristino incompleto: contatta l'assistenza");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="tb-restore-dialog">
        <DialogHeader><DialogTitle>Ripristina «{b.tenant_name}» al {formatDate(b.created_at)}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{AREA_KEYS.map((a) => (
          <label key={a} className="flex items-center gap-2 text-xs"><Checkbox checked={areas.includes(a)} onCheckedChange={() => toggle(a)} data-testid={`tb-area-${a}`} />{a === "files" ? "File e immagini (ripara dal deposito)" : labels[a]}</label>))}</div>
        <p className="text-[11px] text-slate-500">Prima del ripristino viene salvato automaticamente lo stato attuale. Durante l'operazione prenotazioni, ordini e moduli del sito sono sospesi. Le notifiche in coda tornano come annullate (nulla viene reinviato). Le credenziali delle integrazioni non sono nel backup: restano quelle attuali. Lo stato reale dei pagamenti presso il fornitore può differire.</p>
        <Button size="sm" variant="outline" onClick={preview} disabled={busy || !areas.length} data-testid="tb-preview">{busy && !diff ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null}Anteprima differenze</Button>
        {diff && <>
          <DiffTable diff={diff} />
          <label className="text-xs text-slate-600">Per confermare scrivi il nome dell'azienda: <b>{b.tenant_name}</b>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-8" data-testid="tb-confirm-name" /></label>
        </>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="tb-cancel">Annulla</Button>
          <Button variant="destructive" disabled={busy || !diff || name.trim() !== (b.tenant_name || "").trim()} onClick={go} data-testid="tb-restore-confirm">Ripristina</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);
}

export default function TenantBackups() {
  const api = useApi();
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/tenant-backups").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    try { await api.post("/tenant-backups/run", { tenant_id: "" }); toast.success("Backup aziende avviato"); setTimeout(load, 4000); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <Loader2 className="animate-spin text-slate-400" size={16} />;
  const rows = d.backups.filter((b) => !q || b.tenant_name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3" data-testid="tenant-backups">
      <NightlyAlert last={d.last_automatic_at} />
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={run} data-testid="tb-run-now"><ShieldCheck size={13} className="mr-1.5" />Backup aziende ora</Button>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca azienda" className="h-8 w-56 text-xs" data-testid="tb-search" />
        <span className="text-[11px] text-slate-500">Cifrati · conservati 30 giorni · solo amministratori{d.runs[0] ? ` · ultimo: ${d.runs[0].status}` : ""}</span>
      </div>
      <div className="max-h-96 overflow-y-auto rounded-md border border-slate-200">{rows.slice(0, 100).map((b) => (
        <div key={b.id} className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2 text-xs last:border-0" data-testid={`tb-row-${b.id}`}>
          <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{b.tenant_name || b.tenant_id}</span>
          <span className="text-slate-500">{formatDate(b.created_at)} · {b.trigger} · {kb(b.size)} · file {b.vault?.checked || 0}{b.vault?.missing ? ` (${b.vault.missing} mancanti)` : ""}</span>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setSel(b)} data-testid={`tb-restore-${b.id}`}><RotateCcw size={12} className="mr-1" />Ripristina…</Button>
        </div>))}
        {!rows.length && <p className="p-3 text-xs text-slate-500" data-testid="tb-empty">Nessun backup aziende ancora.</p>}
      </div>
      {sel && <RestoreDialog b={sel} labels={d.areas} onClose={() => setSel(null)} onDone={() => { setSel(null); load(); }} />}
    </div>);
}
